// ─── Forfaits — moteur d'autorisation des fonctions (Core, pur) ──────
// Module-AGNOSTIQUE : le core fournit le moteur ; CHAQUE module de la suite
// déclare ses propres fonctions/sous-fonctions (feature-calendar/fonctions.js…)
// et l'app compose le catalogue. Modèle « 3 portes » (porté de MoPCal) :
//   1. désactivation globale (admin)   2. plan/palier + achat à la carte
//   3. désactivation par utilisateur   (+ dépendances).
// Aucune dépendance Firestore/UI ici → testable et réutilisable.

// Paliers d'inclusion (du plus bas au plus haut). `socle` = toujours inclus.
export const PALIERS = [
  { id: 'socle',     label: 'Socle',      ordre: 0 },
  { id: 'basic',     label: 'Basic',      ordre: 1 },
  { id: 'basicplus', label: 'Basic +',    ordre: 2 },
  { id: 'full',      label: 'Full',       ordre: 3 },
  { id: 'ia',        label: 'IA',         ordre: 4 },
];
export const PALIER_ORDRE = PALIERS.reduce((m, p) => { m[p.id] = p.ordre; return m; }, {});
export const palierLabel = (id) => (PALIERS.find(p => p.id === id)?.label || id);

// ─── Achats à la carte ────────────────────────────────────────────
// Format toléré : tableau de clés, OU map { clé: dateFinISO | null } (null = à vie).
export function normaliserAchats(achats) {
  if (!achats) return {};
  if (Array.isArray(achats)) return achats.reduce((m, k) => { m[k] = null; return m; }, {});
  return { ...achats };
}
export function achatsActifs(achats, maintenant) {
  const now = maintenant || null;   // pas de Date.now() implicite : passer une réf si filtrage temporel voulu
  const src = normaliserAchats(achats);
  const out = {};
  for (const k of Object.keys(src)) {
    const fin = src[k];
    if (!fin || !now || fin >= now) out[k] = fin;   // null = à vie ; sinon actif si non expiré
  }
  return out;
}

// ─── Catalogue (composé à partir des modules) ─────────────────────
// Chaque def : { key, parent?, palier, individuel?, icone?, label?, desc?,
//   rubrique?, deps? }. Une sous-fonction (parent défini) dépend AUTO de son parent.
export function construireCatalogue(defs) {
  const list = (defs || []).filter(Boolean);
  const byKey = {};
  for (const d of list) byKey[d.key] = d;
  const depsDe = (key) => {
    const d = byKey[key]; if (!d) return [];
    const base = Array.isArray(d.deps) ? d.deps.slice() : [];
    if (d.parent && !base.includes(d.parent)) base.push(d.parent);
    return base;
  };
  const sousFonctionsDe = (parentKey) => list.filter(d => d.parent === parentKey);
  const racines = list.filter(d => !d.parent);
  return { list, byKey, depsDe, sousFonctionsDe, racines };
}

// ── Poids (achat à la carte par pool de points, 19/08) ─────────────
// Combien de « points » une fonction individuelle coûte dans le pool à la
// carte (cf. pointsALaCarte.js) — distinct du PALIER, qui dit depuis quel
// forfait elle est incluse gratuitement. Réglage en base
// (config/fonctionnalites.poids.{cle}), repli sur le poids déclaré au
// catalogue (`def.poids`), repli final 1 — jamais 0 : un poids nul rendrait
// la fonction gratuite dans le pool, ce qui n'est jamais l'intention d'un
// poids absent.
export function poidsDe(cfg, def) {
  const n = cfg?.poids?.[def?.key];
  if (Number.isFinite(n)) return n;
  return Number.isFinite(def?.poids) ? def.poids : 1;
}

// Fonction incluse par le plan (palier) OU achetée à la carte ?
export function fonctionInclusePlan(def, palierInclus, achats, paliersOverride) {
  if (!def) return true;
  const palier = (paliersOverride && paliersOverride[def.key]) || def.palier || 'basic';
  if (palier === 'socle') return true;
  const ach = achats || {};
  if (ach[def.key] !== undefined) return true;             // acheté à la carte
  if (palierInclus == null) return true;                    // plan non configuré → permissif (sécurité)
  return (PALIER_ORDRE[palier] ?? 99) <= (PALIER_ORDRE[palierInclus] ?? 4);
}

// Moteur complet : renvoie une fonction peutUtiliser(cle) pour un contexte donné.
//   ctx = { catalogue, user, globales, plans, paliers } (tous optionnels sauf catalogue).
export function creerPeutUtiliser(ctx) {
  const { catalogue, user, globales = {}, plans = {}, paliers = {} } = ctx || {};
  const cat = catalogue || construireCatalogue([]);
  const achats = achatsActifs(user?.fonctionnalitesAchetees);
  const palierInclus = plans[user?.plan || 'gratuit']?.palierInclus;
  const bypass = !!(user?.accesIllimite || user?.role === 'admin' || user?.admin);

  // SURCHARGE IVV (valideurs) : quand `ivvSurchargeFonctions` est activé, les
  // activations manuelles (users/{uid}.fonctionnalites) RÉÉCRIVENT le résultat
  // du forfait — true force l'accès même si le plan ne l'inclut pas, false le
  // coupe. Désactivée (défaut, et cas de tous les utilisateurs), le FORFAIT
  // seul décide : les activations manuelles sont ignorées.
  const surcharge = !!user?.ivvSurchargeFonctions;

  const memo = {};
  function peut(cle, pile) {
    if (cle in memo) return memo[cle];
    if (globales[cle] === false) return (memo[cle] = false);
    // Seules les fonctions VITALES resistent a une coupure (aujourd'hui : cet
    // ecran de reglage — sans lui, plus moyen de rien rallumer).
    // PIEGE CORRIGE ICI : le verrou suivait le palier `socle`, si bien qu'une
    // fonction poussee au socle pour etre incluse dans TOUS les forfaits
    // (vacances, gardes…) devenait indesactivable par effet de bord. Inclusion
    // tarifaire et verrou anti-lockout sont deux choses distinctes.
    const estVital = cat.byKey[cle]?.vital === true;
    if (!estVital && surcharge && user?.fonctionnalites?.[cle] === false) return (memo[cle] = false);
    const deps = cat.depsDe(cle);
    for (const dep of deps) {
      if (dep === cle || (pile && pile.includes(dep))) continue;   // garde anti-cycle
      if (!peut(dep, [...(pile || []), cle])) return (memo[cle] = false);
    }
    if (bypass) return (memo[cle] = true);
    if (surcharge && user?.fonctionnalites?.[cle] === true) return (memo[cle] = true);
    const def = cat.byKey[cle];
    return (memo[cle] = fonctionInclusePlan(def, palierInclus, achats, paliers));
  }
  return (cle) => peut(cle);
}
